import * as THREE from "three";

/**
 * EyeFraming.js
 *
 * Per-character dynamic camera framing built from the actual world
 * positions of the LeftEye / RightEye meshes (or bones, when meshes
 * are absent). Replaces the static SCENE_FRAMING.faceAnchorY=180 +
 * fixed Z=95 path, which assumed every character's eyes landed at the
 * same world-Y after pose committal — true for ARKit-style GLBs but
 * routinely false for FBX rigs whose `model.position.y + floorOffset`
 * conflicts with the eye-anchor invariant.
 *
 * The detector walks the model hierarchy, scoring nodes whose name
 * matches an eye pattern, splits them into left/right by name (with a
 * geometric fallback that picks the two extremes along the model's
 * local X axis), and returns world-space anchors plus a face size
 * estimate derived from the inter-pupillary distance.
 *
 * The framing computer turns those anchors into:
 *   - a camera target slightly below the eye midpoint (so the face
 *     center, not the pupils, sits at the viewport center),
 *   - a default camera distance sized from the vertical FOV so the
 *     face occupies a fixed fraction of the viewport,
 *   - close / wide distance bounds for OrbitControls minDistance /
 *     maxDistance, derived from the same face size estimate.
 *
 * All output is in world units (centimeters in this scene).
 */

const ANATOMICAL_FACE_HEIGHT_RATIO = 3.2;
const ANATOMICAL_FACE_WIDTH_RATIO = 4.0;
// Where (in viewport-Y, 0=top, 1=bottom) the top of the head should
// land at the default zoom. Lower values = head sits higher = less
// blank space above; raise to give more headroom.
const HEAD_TOP_VIEWPORT_RATIO = 0.12;
// Default minimum breathing room (scene units = cm) above the head
// and below the chin in the framed bounding box. Per-character
// override via `framingHeadroomMin` on the characterData entry.
const DEFAULT_HEADROOM_MIN = 10;

// Distance ratios — how much of the viewport the face+headroom box
// occupies. Lower = tighter framing (face larger on screen).
// Tightened from {2.0, 3.0, 4.2} so the default zoom leaves less
// dead space above the head while close/wide still bracket it.
const DISTANCE_RATIO_CLOSE = 1.3;
const DISTANCE_RATIO_DEFAULT = 2.4;
const DISTANCE_RATIO_WIDE = 3.36;

const FALLBACK_EYE_DISTANCE = 6.5;

const NAME_EYE_ANY = /eye/i;
const NAME_EYE_LEFT =
    /(^|[_\-\s.])(l|left)[_\-\s.]?eye|eye[_\-\s.]?(l|left)([_\-\s.]|$)/i;
const NAME_EYE_RIGHT =
    /(^|[_\-\s.])(r|right)[_\-\s.]?eye|eye[_\-\s.]?(r|right)([_\-\s.]|$)/i;
const NAME_EYE_EXCLUDE =
    /eyelash|eyebrow|eyeshadow|eyeliner|eyelid|socket|eyewear|glasses|eyetarget|eye_target|eyeaim|eye_aim|eyelookat|eye_look_at|lookat|eyectrl|eye_ctrl|eyectl/i;

// Plausibility band for inter-pupillary distance, in scene units
// (centimeters). Anatomical IPD is ~5.5–7.5 cm; we widen the band
// because the auto-fit pipeline scales every model toward a 165cm
// eye height, so even stylized rigs land in this range. Anything
// outside means the detector latched onto wrong nodes (helpers,
// aim constraints, far-apart head + eye centers, etc.).
const PLAUSIBLE_IPD_MIN = 2.0;
const PLAUSIBLE_IPD_MAX = 25.0;

// Mesh names to ignore when measuring the top-of-head Y. Tongue /
// teeth / interior meshes can poke above the visible silhouette in
// some rigs; helpers / lights / cameras are filtered separately by
// NAME_NON_BODY at call time.
const NAME_HEAD_TOP_EXCLUDE =
    /tongue|teeth|gum|inside|interior|backface|wireframe|bbox|debug|eyelash|eyebrow|eyelid|socket/i;

/**
 * Find the highest world-Y point on any visible body mesh. Used to
 * anchor the camera so the same viewport-Y always lines up with the
 * top of the head — hat, tall hair, nimbus and all — rather than
 * with a synthetic point derived purely from the eye line.
 *
 * Returns null if no usable mesh is found; the caller falls back to
 * `midpoint.y + faceHeight * 0.5`.
 */
export function detectHeadTopY(model, opts = null) {
    if (!model || typeof model.traverse !== "function") return null;
    try {
        model.updateMatrixWorld(true);
    } catch (_e) {
        /* noop */
    }
    // Only consider meshes whose top sits above the eye midpoint —
    // otherwise a raised arm, prop, or torso geometry above an
    // unusually low pivot can dominate the max-Y and pull the
    // framing off the head.
    const minY = opts && Number.isFinite(opts.minY) ? opts.minY : -Infinity;
    let maxY = -Infinity;
    let count = 0;
    model.traverse((node) => {
        if (!node || !node.isMesh || !node.visible) return;
        const name = node.name || "";
        if (NAME_NON_BODY.test(name)) return;
        if (NAME_HEAD_TOP_EXCLUDE.test(name)) return;
        try {
            const box = new THREE.Box3().setFromObject(node);
            if (
                !box.isEmpty() &&
                Number.isFinite(box.max.y) &&
                box.max.y > minY &&
                box.max.y > maxY
            ) {
                maxY = box.max.y;
            }
            count++;
        } catch (_e) {
            /* skip */
        }
    });
    if (count === 0 || !Number.isFinite(maxY)) return null;
    return maxY;
}

function nodeWorldCenter(node, out) {
    out = out || new THREE.Vector3();
    if (!node) {
        out.set(0, 0, 0);
        return out;
    }
    if (node.isMesh && node.geometry) {
        try {
            const box = new THREE.Box3().setFromObject(node);
            if (Number.isFinite(box.min.x) && Number.isFinite(box.max.x)) {
                box.getCenter(out);
                return out;
            }
        } catch (_e) {
            /* fall through to world position */
        }
    }
    try {
        node.getWorldPosition(out);
    } catch (_e) {
        out.set(0, 0, 0);
    }
    return out;
}

/**
 * Detect left/right eye anchors on a loaded model.
 *
 * @param {THREE.Object3D} model
 * @returns {{
 *   leftEye: THREE.Vector3,
 *   rightEye: THREE.Vector3,
 *   midpoint: THREE.Vector3,
 *   eyeDistance: number,
 *   faceHeight: number,
 *   faceWidth: number,
 *   source: string,
 *   confidence: number
 * } | null}
 */
export function detectEyeAnchors(model) {
    if (!model || typeof model.traverse !== "function") return null;
    try {
        model.updateMatrixWorld(true);
    } catch (_e) {
        /* noop */
    }

    const leftCandidates = [];
    const rightCandidates = [];
    const genericEyeCandidates = [];

    model.traverse((node) => {
        if (!node || !node.name) return;
        const name = node.name;
        if (NAME_EYE_EXCLUDE.test(name)) return;
        if (!NAME_EYE_ANY.test(name)) return;

        if (NAME_EYE_LEFT.test(name)) {
            leftCandidates.push(node);
        } else if (NAME_EYE_RIGHT.test(name)) {
            rightCandidates.push(node);
        } else {
            genericEyeCandidates.push(node);
        }
    });

    const tmp = new THREE.Vector3();
    const averageWorldCenter = (nodes) => {
        if (!nodes || nodes.length === 0) return null;
        const acc = new THREE.Vector3();
        let count = 0;
        for (const n of nodes) {
            nodeWorldCenter(n, tmp);
            if (
                Number.isFinite(tmp.x) &&
                Number.isFinite(tmp.y) &&
                Number.isFinite(tmp.z)
            ) {
                acc.add(tmp);
                count++;
            }
        }
        if (count === 0) return null;
        acc.multiplyScalar(1 / count);
        return acc;
    };
    const nodeNamesOf = (nodes) =>
        (nodes || [])
            .map((n) => n && n.name)
            .filter(Boolean)
            .join("+") || null;

    let left = averageWorldCenter(leftCandidates);
    let right = averageWorldCenter(rightCandidates);
    let leftNodeName = nodeNamesOf(leftCandidates);
    let rightNodeName = nodeNamesOf(rightCandidates);
    let source = "mesh-pair";
    let confidence = 1.0;

    // Geometric fallback: split unlabeled "eye" nodes by their position
    // along the model's local X axis. Useful for rigs that name both
    // eyes "Eye" or use mesh-internal naming we don't recognize.
    if ((!left || !right) && genericEyeCandidates.length >= 2) {
        const modelInverseQuat = new THREE.Quaternion();
        try {
            model.getWorldQuaternion(modelInverseQuat).invert();
        } catch (_e) {
            modelInverseQuat.identity();
        }
        const modelWorldPos = new THREE.Vector3();
        try {
            model.getWorldPosition(modelWorldPos);
        } catch (_e) {
            modelWorldPos.set(0, 0, 0);
        }
        const decorated = genericEyeCandidates.map((n) => {
            const wc = nodeWorldCenter(n, new THREE.Vector3());
            const local = wc
                .clone()
                .sub(modelWorldPos)
                .applyQuaternion(modelInverseQuat);
            return { node: n, worldCenter: wc, localX: local.x };
        });
        decorated.sort((a, b) => a.localX - b.localX);
        // Model-local X: in three.js a left-handed-style convention,
        // the model's right side typically has positive X (the camera
        // sits on +Z and looks down -Z; right-of-camera becomes the
        // model's left side from its own POV). We label by world X
        // sign rather than enforcing anatomical correctness — the
        // framing math is symmetric so the L/R labelling only matters
        // for downstream callers, none of which depend on it today.
        const lowest = decorated[0];
        const highest = decorated[decorated.length - 1];
        if (!left) {
            left = lowest.worldCenter.clone();
            leftNodeName = lowest.node && lowest.node.name;
        }
        if (!right) {
            right = highest.worldCenter.clone();
            rightNodeName = highest.node && highest.node.name;
        }
        source = "generic-eye-split";
        confidence = 0.7;
    }

    // Single-eye mirror fallback: if we have one eye, mirror across
    // the model's local YZ plane to estimate the other.
    if ((left && !right) || (!left && right)) {
        const known = left || right;
        const modelWorldPos = new THREE.Vector3();
        const modelQuat = new THREE.Quaternion();
        try {
            model.getWorldPosition(modelWorldPos);
            model.getWorldQuaternion(modelQuat);
        } catch (_e) {
            /* identity */
        }
        const invQuat = modelQuat.clone().invert();
        const local = known.clone().sub(modelWorldPos).applyQuaternion(invQuat);
        const mirroredLocal = local.clone();
        mirroredLocal.x = -mirroredLocal.x;
        const mirroredWorld = mirroredLocal
            .applyQuaternion(modelQuat)
            .add(modelWorldPos);
        if (left) {
            right = mirroredWorld;
            rightNodeName = (leftNodeName || "?") + "(mirrored)";
        } else {
            left = mirroredWorld;
            leftNodeName = (rightNodeName || "?") + "(mirrored)";
        }
        source = "single-eye-mirror";
        confidence = 0.6;
    }

    if (!left || !right) {
        return null;
    }

    const eyeDistance = left.distanceTo(right);
    if (!Number.isFinite(eyeDistance) || eyeDistance <= 0.01) {
        return null;
    }
    // Sanity-band: an implausibly small or large IPD almost always
    // means the detector latched onto helper nodes (eye-aim targets,
    // single-mesh combined eye, head + eye centers averaged together).
    // Reject so the caller falls back to fallbackAnchorsFromHead.
    if (eyeDistance < PLAUSIBLE_IPD_MIN || eyeDistance > PLAUSIBLE_IPD_MAX) {
        console.warn(
            `[EyeFraming] rejecting implausible IPD=${eyeDistance.toFixed(2)} ` +
                `(source=${source}); falling back`,
        );
        return null;
    }

    const midpoint = left.clone().add(right).multiplyScalar(0.5);
    const faceHeight = eyeDistance * ANATOMICAL_FACE_HEIGHT_RATIO;
    const faceWidth = eyeDistance * ANATOMICAL_FACE_WIDTH_RATIO;
    // Constrain the head-top search to meshes above the eye midpoint
    // so a raised arm or prop can't pull the max-Y off the head.
    const headTopY = detectHeadTopY(model, { minY: midpoint.y });

    return {
        leftEye: left,
        rightEye: right,
        midpoint,
        eyeDistance,
        faceHeight,
        faceWidth,
        headTopY,
        source,
        confidence,
        leftNodeName,
        rightNodeName,
    };
}

// Artist-placed explicit framing anchor. When a character's GLB
// contains a node named exactly `EyeAnchor` (also accepts the
// `Eye_Anchor` variant, case-insensitive), the framing pipeline can
// be told via the per-character `useEyeAnchor: true` flag to use its
// world position as the eye midpoint and skip every heuristic. See
// `detectExplicitEyeAnchor` and the Gotchas entry in replit.md.
const NAME_EYE_ANCHOR = /^eye[_]?anchor$/i;

/**
 * Look for an artist-placed `EyeAnchor` (or `Eye_Anchor`) node in the
 * model and, if found, return a normal anchors object whose midpoint
 * is its world position. The IPD is set to a plausible default and
 * the left/right eye world positions are synthesized along the
 * model's local X axis so downstream framing math (face width,
 * azimuth clamp, mirror fallbacks) all keeps working.
 *
 * Returns null if no such node exists. Caller decides whether to
 * fall back to heuristics.
 */
export function detectExplicitEyeAnchor(model) {
    if (!model || typeof model.traverse !== "function") return null;
    try {
        model.updateMatrixWorld(true);
    } catch (_e) {
        /* noop */
    }
    let anchorNode = null;
    model.traverse((node) => {
        if (anchorNode || !node || !node.name) return;
        if (NAME_EYE_ANCHOR.test(node.name)) {
            anchorNode = node;
        }
    });
    if (!anchorNode) return null;

    const midpoint = new THREE.Vector3();
    try {
        anchorNode.getWorldPosition(midpoint);
    } catch (_e) {
        return null;
    }
    if (
        !Number.isFinite(midpoint.x) ||
        !Number.isFinite(midpoint.y) ||
        !Number.isFinite(midpoint.z)
    ) {
        return null;
    }

    // Synthesize plausible left/right eye world positions along the
    // model's local X axis, so existing eye-tracking + mirror code
    // paths keep working without special-casing this anchor.
    const ipd = FALLBACK_EYE_DISTANCE;
    const modelQuat = new THREE.Quaternion();
    try {
        model.getWorldQuaternion(modelQuat);
    } catch (_e) {
        modelQuat.identity();
    }
    const xAxisWorld = new THREE.Vector3(1, 0, 0).applyQuaternion(modelQuat);
    const halfOffset = xAxisWorld.clone().multiplyScalar(ipd * 0.5);
    const leftEye = midpoint.clone().sub(halfOffset);
    const rightEye = midpoint.clone().add(halfOffset);

    const faceHeight = ipd * ANATOMICAL_FACE_HEIGHT_RATIO;
    const faceWidth = ipd * ANATOMICAL_FACE_WIDTH_RATIO;
    // Constrain head-top search to meshes above the anchor so an arm
    // or prop above an unusually low pivot can't pull max-Y off head.
    const headTopY = detectHeadTopY(model, { minY: midpoint.y });

    return {
        leftEye,
        rightEye,
        midpoint,
        eyeDistance: ipd,
        faceHeight,
        faceWidth,
        headTopY,
        source: "eye-anchor",
        confidence: 1.0,
        leftNodeName: anchorNode.name + "(synthL)",
        rightNodeName: anchorNode.name + "(synthR)",
    };
}

const NAME_HEAD = /head(?!less|set|phones)/i;
const NAME_FACE = /\bface\b|\bskull\b/i;
const NAME_NON_BODY =
    /helper|empty|target|ctrl|aim|lookat|null|locator|root|origin|light|camera/i;

function buildAnchorsFromBox(box, sourceTag, model, ipdHint) {
    if (!box || box.isEmpty()) return null;
    const size = new THREE.Vector3();
    box.getSize(size);
    if (!Number.isFinite(size.x) || size.x <= 0) return null;
    // Head/face boxes: eyes sit ~30% below the top of the head along Y.
    // Full-body boxes: eyes sit at upper third (~85% up from feet).
    const isUpperThird = sourceTag === "fallback-upper-third-bbox";
    const eyeY = isUpperThird
        ? box.min.y + size.y * 0.85
        : box.max.y - size.y * 0.3;
    const center = new THREE.Vector3();
    box.getCenter(center);
    // IPD ≈ 40% of head width; for the body-bbox fallback, scale a
    // sensible IPD off character height instead.
    let ipd =
        Number.isFinite(ipdHint) && ipdHint > 0
            ? ipdHint
            : isUpperThird
              ? Math.max(FALLBACK_EYE_DISTANCE, size.y * 0.04)
              : Math.max(FALLBACK_EYE_DISTANCE, size.x * 0.4);
    // Clamp to plausibility band so the framing math stays sane.
    if (ipd < PLAUSIBLE_IPD_MIN) ipd = PLAUSIBLE_IPD_MIN;
    if (ipd > PLAUSIBLE_IPD_MAX) ipd = PLAUSIBLE_IPD_MAX;

    // Place left/right along the model's local X axis so the
    // anchors rotate with `modelRotationY` overrides.
    const xAxisLocal = new THREE.Vector3(1, 0, 0);
    const modelQuat = new THREE.Quaternion();
    try {
        model.getWorldQuaternion(modelQuat);
    } catch (_e) {
        modelQuat.identity();
    }
    const xAxisWorld = xAxisLocal.applyQuaternion(modelQuat);
    const halfOffset = xAxisWorld.clone().multiplyScalar(ipd * 0.5);

    const midpoint = new THREE.Vector3(center.x, eyeY, center.z);
    const left = midpoint.clone().sub(halfOffset);
    const right = midpoint.clone().add(halfOffset);
    return {
        leftEye: left,
        rightEye: right,
        midpoint,
        eyeDistance: ipd,
        faceHeight: ipd * ANATOMICAL_FACE_HEIGHT_RATIO,
        faceWidth: ipd * ANATOMICAL_FACE_WIDTH_RATIO,
        headTopY: box.max.y,
        source: sourceTag,
        confidence: isUpperThird ? 0.3 : 0.5,
    };
}

/**
 * Fallback anchor chain when no LeftEye / RightEye nodes are found:
 *   1. head bone (Object3D matching /head/i, no geometry) — derive
 *      eye box from the head bone's world position + a small upward
 *      offset and a skull-width IPD.
 *   2. head / face mesh — Box3 of the matching mesh.
 *   3. upper-third visible-mesh bounding box of the entire model.
 *
 * Returns a normal anchors object with a `source` tag prefixed
 * `fallback-...` so the caller knows to suppress eye-tracking.
 */
export function fallbackAnchorsFromHead(model) {
    if (!model) return null;
    try {
        model.updateMatrixWorld(true);
    } catch (_e) {
        /* noop */
    }

    // 1) Head bone — find any non-mesh (or geometry-less) node whose
    //    name matches /head/i. Build a tiny synthetic box around its
    //    world position so the framing math has a head-sized target.
    let headBone = null;
    model.traverse((node) => {
        if (headBone || !node || !node.name) return;
        if (NAME_NON_BODY.test(node.name)) return;
        if (!NAME_HEAD.test(node.name)) return;
        if (node.isMesh && node.geometry) return; // prefer bones first
        headBone = node;
    });
    if (headBone) {
        const wp = new THREE.Vector3();
        try {
            headBone.getWorldPosition(wp);
        } catch (_e) {
            /* skip */
        }
        if (Number.isFinite(wp.x)) {
            // Head bone usually sits at the base of the skull; the
            // eyes are ~10cm above. Build a 16x18x16 box around it.
            const box = new THREE.Box3(
                new THREE.Vector3(wp.x - 8, wp.y - 4, wp.z - 8),
                new THREE.Vector3(wp.x + 8, wp.y + 14, wp.z + 8),
            );
            const a = buildAnchorsFromBox(box, "fallback-head-bone", model, 7);
            if (a) return a;
        }
    }

    // 2) Head / face mesh — Box3 around the first mesh whose name
    //    matches head or face (excluding hair/cap/lash/etc).
    let headMesh = null;
    model.traverse((node) => {
        if (headMesh || !node || !node.name) return;
        if (!node.isMesh) return;
        if (NAME_EYE_EXCLUDE.test(node.name)) return;
        if (NAME_HEAD.test(node.name) || NAME_FACE.test(node.name)) {
            headMesh = node;
        }
    });
    if (headMesh) {
        try {
            const box = new THREE.Box3().setFromObject(headMesh);
            const a = buildAnchorsFromBox(box, "fallback-head-mesh", model);
            if (a) return a;
        } catch (_e) {
            /* fall through */
        }
    }

    // 3) Upper-third bounding box of all visible meshes.
    try {
        const box = new THREE.Box3();
        let any = false;
        model.traverse((node) => {
            if (!node || !node.isMesh || !node.visible) return;
            if (NAME_NON_BODY.test(node.name || "")) return;
            try {
                const meshBox = new THREE.Box3().setFromObject(node);
                if (!meshBox.isEmpty()) {
                    box.union(meshBox);
                    any = true;
                }
            } catch (_e) {
                /* skip */
            }
        });
        if (any) {
            const a = buildAnchorsFromBox(
                box,
                "fallback-upper-third-bbox",
                model,
            );
            if (a) return a;
        }
    } catch (_e) {
        /* noop */
    }

    return null;
}

/**
 * Compute camera framing (target + position + zoom bounds) from a set
 * of detected eye anchors and the active camera's vertical FOV.
 *
 * The camera is placed straight in front of the eye midpoint along
 * world +Z (characters face the camera by default at rotation.y=0;
 * per-character override via characterData.modelRotationY rotates the
 * model rather than the camera). Camera Y matches target Y so the
 * gaze line is dead horizontal.
 *
 * @param {object} anchors    output of detectEyeAnchors / fallback
 * @param {object} cameraOpts { fovDegrees, aspect, isMobile }
 * @returns {{
 *   target: THREE.Vector3,
 *   cameraPosition: THREE.Vector3,
 *   distanceClose: number,
 *   distanceDefault: number,
 *   distanceWide: number,
 *   faceHeight: number,
 *   faceWidth: number
 * }}
 */
export function computeFramingFromAnchors(anchors, cameraOpts) {
    const fovDeg = (cameraOpts && cameraOpts.fovDegrees) || 50;
    const aspect = (cameraOpts && cameraOpts.aspect) || 16 / 9;
    const fovRad = (fovDeg * Math.PI) / 180;
    const tanHalfV = Math.tan(fovRad * 0.5);
    const tanHalfH = tanHalfV * aspect;

    const faceHeight = anchors.faceHeight;
    const faceWidth = anchors.faceWidth;

    // Per-character knobs (with safe defaults).
    const headroomMin =
        cameraOpts &&
        Number.isFinite(cameraOpts.framingHeadroomMin) &&
        cameraOpts.framingHeadroomMin >= 0
            ? cameraOpts.framingHeadroomMin
            : DEFAULT_HEADROOM_MIN;
    const yOffset =
        cameraOpts && Number.isFinite(cameraOpts.framingYOffset)
            ? cameraOpts.framingYOffset
            : 0;
    const headroomOverride = !!(
        cameraOpts &&
        Number.isFinite(cameraOpts.framingHeadroomMin) &&
        cameraOpts.framingHeadroomMin !== DEFAULT_HEADROOM_MIN
    );
    const yOffsetOverride = yOffset !== 0;

    // Use the measured top-of-head when available so hats / tall hair
    // get included in the frame; otherwise estimate from the eye line.
    const chinY = anchors.midpoint.y - faceHeight * 0.55;
    const headTopMeasured = Number.isFinite(anchors.headTopY);
    const headTopY = headTopMeasured
        ? anchors.headTopY
        : anchors.midpoint.y + faceHeight * 0.5;
    const headTopSource = headTopMeasured ? "measured" : "estimated";
    const measuredFaceH = Math.max(0, headTopY - chinY);
    const effectiveHeight = Math.max(faceHeight, measuredFaceH);
    const totalH = effectiveHeight + 2 * headroomMin;

    const distFor = (ratio) => {
        const dV = (totalH * ratio) / (2 * tanHalfV);
        const dH = (faceWidth * ratio) / (2 * tanHalfH);
        // Use whichever axis crops first so the face never spills off
        // the viewport in portrait orientations.
        return Math.max(dV, dH);
    };

    const distanceClose = distFor(DISTANCE_RATIO_CLOSE);
    const distanceDefault = distFor(DISTANCE_RATIO_DEFAULT);
    const distanceWide = distFor(DISTANCE_RATIO_WIDE);

    // Target Y solves: project headTopY to viewport-Y =
    // HEAD_TOP_VIEWPORT_RATIO at distanceDefault, with camera Y
    // matching target Y. Solving for ty:
    //   ty = headTopY - (0.5 - HEAD_TOP_VIEWPORT_RATIO)
    //                 * 2 * D * tan(fovV/2)
    // This puts the same pixel-Y on every character's headtop
    // regardless of body size, hat, or pivot location.
    const target = anchors.midpoint.clone();
    const tyBase =
        headTopY -
        (0.5 - HEAD_TOP_VIEWPORT_RATIO) * 2 * distanceDefault * tanHalfV;
    // Sign convention: positive `framingYOffset` should move the
    // portrait UP in the viewport. Camera Y tracks target Y, so
    // RAISING target Y raises the camera with it and the model
    // visually slides DOWN. Subtract yOffset so positive values
    // give the documented "shift up" behavior.
    target.y = tyBase - yOffset;

    // Camera goes in front of the face along the character's local
    // +Z (the back side of three.js's default forward axis -Z),
    // rotated by per-character `modelRotationY` so portraits authored
    // facing +X / -Z still get framed from the correct side.
    const rotY =
        cameraOpts && Number.isFinite(cameraOpts.modelRotationY)
            ? cameraOpts.modelRotationY
            : 0;
    const forward = new THREE.Vector3(Math.sin(rotY), 0, Math.cos(rotY));
    const cameraPosition = target
        .clone()
        .add(forward.clone().multiplyScalar(distanceDefault));

    // Camera Y matches target Y for a horizontal eye-line.
    cameraPosition.y = target.y;

    // Face-locked zoom target: eye midpoint shifted slightly down so
    // the close-up frame puts the eyes in the upper-middle band and
    // the mouth/chin remain visible. This is independent of the
    // head-top anchor used by the talking-frame target, so hats and
    // tall hair don't push the close-zoom face off-center.
    const faceTarget = anchors.midpoint.clone();
    faceTarget.y = anchors.midpoint.y - faceHeight * 0.12;

    return {
        target,
        cameraPosition,
        forward,
        faceTarget,
        distanceClose,
        distanceDefault,
        distanceWide,
        faceHeight,
        faceWidth,
        effectiveHeight,
        headTopY,
        headTopSource,
        chinY,
        headroomMin,
        headroomOverride,
        yOffset,
        yOffsetOverride,
    };
}

export const EYE_FRAMING_CONSTANTS = Object.freeze({
    ANATOMICAL_FACE_HEIGHT_RATIO,
    ANATOMICAL_FACE_WIDTH_RATIO,
    HEAD_TOP_VIEWPORT_RATIO,
    DEFAULT_HEADROOM_MIN,
    DISTANCE_RATIO_CLOSE,
    DISTANCE_RATIO_DEFAULT,
    DISTANCE_RATIO_WIDE,
});
